import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home, Square, CircleDot, Divide, LineChart, TrendingUp, Infinity, Asterisk,
  ScanFace, Shuffle, Percent, Cuboid, Calculator, Ruler, MapPin, FunctionSquare,
  ChevronsUp, ArrowLeftRight, DivideCircle, Dice1, Text, Calendar, GraduationCap,
  Weight, Droplet, Salad, Egg, Banana, Apple, HeartPulse, Baby, ClipboardEdit,
  CalendarHeart, BookCheck, Cigarette, ScrollText, Scale, Brain, Thermometer,
  TrendingDown, BookMarked, Coins, Sun, Moon, Search, X, ChevronUp, ChevronDown
} from 'lucide-react';
import { useTheme } from '../App';
import { sidebarCategories } from '../data/calculatorCategories';
import { useLanguage } from '../i18n/LanguageContext';
import { getUrlForId, routeConfig } from '../i18n/routes';


// URL formatları (Artık merkezi dosyadan alınıyor)




interface SidebarProps {
  calculators: { id: string; name: string; icon: string }[];
  activeCalculator: string;
  setActiveCalculator: (id: string) => void;
  onCloseMobile?: () => void;
}

// Özel hesaplayıcı logosu bileşeni
const CalculatorLogo: React.FC<{ size?: 'xs' | 'sm' | 'md' }> = ({ size = 'sm' }) => {
  const dimensions = {
    xs: {
      container: 'h-6 w-6 rounded-md',
      screen: 'h-1',
      screenTop: 'top-0.5 left-0.5 right-0.5',
      buttonsContainer: 'space-x-0.5',
      button: 'h-0.5 w-0.5',
      iconSize: 10,
    },
    sm: {
      container: 'h-8 w-8 rounded-md',
      screen: 'h-1.5',
      screenTop: 'top-0.5 left-0.5 right-0.5',
      buttonsContainer: 'space-x-0.5',
      button: 'h-1 w-1',
      iconSize: 12,
    },
    md: {
      container: 'h-12 w-12 rounded-lg',
      screen: 'h-2',
      screenTop: 'top-1 left-1 right-1',
      buttonsContainer: 'space-x-1',
      button: 'h-1.5 w-1.5',
      iconSize: 16,
    }
  };

  const d = dimensions[size];

  return (
    <div className={`relative ${d.container} bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-md`}>
      <div className={`absolute ${d.screenTop} ${d.screen} bg-cyan-300/30 rounded-t-sm`}></div>
      <div className="flex flex-col items-center">
        <div className={`flex ${d.buttonsContainer} mb-0.5`}>
          <div className={`${d.button} bg-white rounded-full`}></div>
          <div className={`${d.button} bg-white rounded-full`}></div>
          <div className={`${d.button} bg-white rounded-full`}></div>
        </div>
        <Calculator size={d.iconSize} className="text-white" />
      </div>
    </div>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ calculators, activeCalculator, setActiveCalculator, onCloseMobile }) => {
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { language, t, getLink } = useLanguage();

  // Arama terimi değiştiğinde tüm kategorileri aç
  useEffect(() => {
    if (searchTerm) {
      setOpenCategory(null); // Arama yapılırken kategori filtresi kaldır
    }
  }, [searchTerm]);

  const toggleCategory = (category: string) => {
    if (openCategory === category) {
      setOpenCategory(null);
    } else {
      setOpenCategory(category);
    }
  };

  const handleCalculatorClick = (id: string) => {
    setActiveCalculator(id);

    // Mobile sidebar close logic
    if (onCloseMobile) {
      onCloseMobile();
    } else {
      // Fallback to DOM manipulation if prop not provided (backward compatibility)
      const sidebar = document.getElementById('mobile-sidebar');
      if (sidebar && window.innerWidth < 768) {
        sidebar.classList.add('hidden');
      }
    }
  };

  // URL oluşturmak için yardımcı fonksiyon
  const getHref = (id: string) => {
    return getLink(id);
  };

  // SEO için a href tag'i içeren özel Link bileşeni
  const CalculatorLink = ({ id, children, className }: { id: string, children: React.ReactNode, className?: string }) => {
    const href = getHref(id);
    const isExternal = href.startsWith('http');
    return (
      <a
        href={href}
        onClick={(e) => {
          if (isExternal) {
            // Subdomain veya harici URL — tarayıcı doğal navigasyonu kullanır
            return;
          }
          e.preventDefault();
          handleCalculatorClick(id);
        }}
        className={className}
      >
        {children}
      </a>
    );
  };

  // Filtreleme fonksiyonu - arama terimini içeren hesaplayıcıları filtrele
  const filterCalculators = (calcList: Array<{ id: string; name: string; icon: string }>) => {
    if (!searchTerm.trim()) return calcList;

    return calcList.filter(calc =>
      calc.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getIcon = (iconName: string, size = 16) => {
    switch (iconName) {
      case 'home':
        return <Home size={size} />;
      case 'square':
        return <Square size={size} />;
      case 'circle-dot':
        return <CircleDot size={size} />;
      case 'divide':
        return <Divide size={size} />;
      case 'chart-line-up':
        return <LineChart size={size} />;
      case 'trending-up':
        return <TrendingUp size={size} />;
      case 'square-dashed':
        return <Square size={size} />;
      case 'infinity':
        return <Infinity size={size} />;
      case 'asterisk':
        return <Asterisk size={size} />;
      case 'scan-face':
        return <ScanFace size={size} />;
      case 'shuffle':
        return <Shuffle size={size} />;
      case 'percent':
        return <Percent size={size} />;
      case 'cube':
        return <Cuboid size={size} />;
      case 'calculator':
        return <Calculator size={size} />;
      case 'ruler':
        return <Ruler size={size} />;
      case 'map-pin':
        return <MapPin size={size} />;
      case 'function-square':
        return <FunctionSquare size={size} />;
      case 'chevrons-up':
        return <ChevronsUp size={size} />;
      case 'arrow-left-right':
        return <ArrowLeftRight size={size} />;
      case 'divide-circle':
        return <DivideCircle size={size} />;
      case 'dice-1':
        return <Dice1 size={size} />;
      case 'text':
        return <Text size={size} />;
      case 'calendar':
        return <Calendar size={size} />;
      case 'graduation-cap':
        return <GraduationCap size={size} />;
      case 'weight':
        return <Weight size={size} />;
      case 'droplet':
        return <Droplet size={size} />;
      case 'salad':
        return <Salad size={size} />;
      case 'egg':
        return <Egg size={size} />;
      case 'banana':
        return <Banana size={size} />;
      case 'apple':
        return <Apple size={size} />;
      case 'heart-pulse':
        return <HeartPulse size={size} />;
      case 'baby':
        return <Baby size={size} />;
      case 'clipboard-edit':
        return <ClipboardEdit size={size} />;
      case 'ruler-square':
        return <Ruler size={size} />;
      case 'calendar-heart':
        return <CalendarHeart size={size} />;
      case 'book-check':
        return <BookCheck size={size} />;
      case 'cigar':
        return <Cigarette size={size} />;
      case 'stretching':
        return <ScrollText size={size} />;
      case 'scales':
        return <Scale size={size} />;
      case 'brain':
        return <Brain size={size} />;
      case 'thermometer':
        return <Thermometer size={size} />;
      case 'trending-down':
        return <TrendingDown size={size} />;
      case 'book-marked':
        return <BookMarked size={size} />;
      case 'gold':
        return <Coins size={size} />;
      case 'coins':
        return <Coins size={size} />;
      default:
        return <CircleDot size={size} />;
    }
  };

  // Her kategori için hangi calculator ID'lerinin dahil edileceğini belirleyen fonksiyon
  const getCategoryCalculatorIds = (categoryId: string): string[] => {
    switch (categoryId) {
      case 'math':
        return ['factorial', 'gcdlcm', 'primeFactor', 'combination', 'permutation',
          'percentage', 'ratio', 'sqrt', 'power', 'modular', 'random',
          'pronunciation', 'stdDev'];
      case 'finance':
        return ['priceIncrease', 'goldCalculator', 'cryptoCalculator', 'cryptoCurrencyConverter'];
      case 'islamic':
        return ['zekat'];
      case 'geometry':
        return ['area', 'volume', 'perimeter', 'goldenRatio'];
      case 'measurement':
        return ['squareMeter', 'inch', 'mile', 'baseConversion'];
      case 'time':
        return ['daysCalculator', 'birthDate', 'yasHesaplama', 'yoldaGecenZaman'];
      case 'education':
        return ['gradeCalculator', 'gpaCalculator', 'liseMezuniyet', 'tusPuan', 'tytPuan', 'animalSounds'];
      case 'health':
        return ['waterNeed', 'idealWeight', 'calorieNeed', 'macroNutrient',
          'proteinNeed', 'carbNeed', 'fatNeed', 'bmr', 'bmi', 'bodyFat',
          'waistHipRatio', 'babyHeight', 'babyWeight', 'metabolizmaYasi', 'yumurtlamaDonemi'];
      case 'tools':
        return ['wordCount', 'pronunciation', 'loremIpsum', 'dataUnitConverter', 'weightUnitConverter',
          'energyUnitConverter', 'lengthUnitConverter', 'jsonFormatter', 'textCaseConverter',
          'urlEncodeDecoder', 'base64EncodeDecode', 'uuidGenerator', 'hashCalculator',
          'qrCodeGenerator', 'imageResizer', 'textFormatter', 'passwordGenerator',
          'colorPicker', 'timeZoneConverter', 'cssMinifier', 'jsBeautifier',
          'textToASCII', 'imageCompressor', 'imageFormatConverter', 'faviconGenerator'];
      case 'homeAppliances':
        return ['klimaBtu', 'elektrikFaturasi', 'dogalgazFaturasi'];
      case 'automotive':
        return ['fuelConsumption'];
      case 'income':
        return ['dailyRentalIncome'];
      default:
        return [];
    }
  };

  // Kategori bazlı hesaplayıcılar - merkezi dosyadan import ediliyor
  const categories = sidebarCategories.map(category => {
    // Eğer kategori calculators prop'u varsa, onu kullan
    if (category.calculators && category.calculators.length > 0) {
      return {
        ...category,
        calculators: category.calculators.map((calc: any) => ({
          ...calc,
          icon: typeof calc.icon === 'string' ? calc.icon : getIcon(calc.icon?.toString() || 'circle-dot')
        }))
      };
    }

    // Yoksa mevcut calculators array'inden filtrele
    return {
      ...category,
      calculators: calculators.filter((calc: any) => {
        // Her kategori için hangi calculator ID'lerinin dahil edileceğini belirle
        const categoryCalculatorIds = getCategoryCalculatorIds(category.id);
        return categoryCalculatorIds.includes(calc.id);
      })
    };
  });

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <a
          href="/"
          className="flex items-center cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            setActiveCalculator('home');
            navigate('/');
            // Hide mobile sidebar after clicking
            if (onCloseMobile) {
              onCloseMobile();
            } else {
              const sidebar = document.getElementById('mobile-sidebar');
              if (sidebar) {
                sidebar.classList.add('hidden');
              }
            }
          }}
        >
          <div className="mr-3">
            <CalculatorLogo size="xs" />
          </div>
          <h1 className="text-xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">
            {t('common.title')}
          </h1>
        </a>
        <button
          onClick={toggleTheme}
          className="p-2 rounded-full bg-cyan-900/50 dark:text-yellow-300 text-slate-700
                    hover:bg-cyan-800/50 transition-colors"
          aria-label={theme === 'dark' ? t('common.toggleLight') : t('common.toggleDark')}
          title={theme === 'dark' ? t('common.toggleLight') : t('common.toggleDark')}
        >
          {theme === 'dark' ? (
            <Sun size={18} />
          ) : (
            <Moon size={18} />
          )}
        </button>
      </div>

      {/* Arama Kutusu */}
      <div className="relative mb-4">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search size={16} className="text-cyan-500" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t('common.search')}
          className="w-full py-2 pl-10 pr-3 bg-[var(--card-bg)] border border-[var(--border-color)] 
                   rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <nav className="overflow-y-auto">
        {searchTerm ? (
          // Arama sonuçları
          <div className="space-y-1">
            <h3 className="text-xs uppercase text-cyan-500/70 font-semibold px-3 pt-1 pb-2">
              {t('common.search_results')}
            </h3>
            {filterCalculators(calculators).length > 0 ? (
              filterCalculators(calculators).map((calc) => (
                <CalculatorLink
                  key={calc.id}
                  id={calc.id}
                  className={`flex items-center w-full text-left px-3 py-2 text-sm rounded
                    ${activeCalculator === calc.id
                      ? 'bg-cyan-900/40 text-cyan-300 border-l-2 border-cyan-400 dark:bg-cyan-900/40 dark:text-cyan-300 bg-cyan-100 text-cyan-900 border-cyan-600'
                      : 'text-gray-600 dark:text-gray-400 hover:text-cyan-900 dark:hover:text-cyan-200 hover:bg-gray-200 dark:hover:bg-[var(--hover-bg)]'} 
                    transition-all duration-200`}
                >
                  <span className="mr-2 opacity-70">
                    {getIcon(calc.icon, 16)}
                  </span>
                  {calc.name}
                </CalculatorLink>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500 text-sm">
                {t('common.no_results_found')}
              </div>
            )}
          </div>
        ) : (
          <ul className="space-y-2">
            {/* Ana Sayfa always listed */}
            <li className="mb-1">
              <CalculatorLink
                id="home"
                className={`w-full text-left px-3 py-2 rounded flex items-center text-sm 
                  ${activeCalculator === 'home'
                    ? 'bg-cyan-900/40 text-cyan-300 border-l-2 border-cyan-400 dark:bg-cyan-900/40 dark:text-cyan-300 bg-cyan-100 text-cyan-900 border-cyan-600'
                    : 'hover:bg-gray-200 dark:hover:bg-[var(--hover-bg)] text-gray-600 dark:text-gray-400 hover:text-cyan-900 dark:hover:text-cyan-200'} 
                  transition-all duration-200`}
              >
                <span className="mr-2 opacity-80">
                  <Home size={20} className="text-cyan-400" />
                </span>
                {t('common.home')}
              </CalculatorLink>
            </li>

            {/* If Language is NL, Only show specific calculators without categories for simplicity, or filtered categories */}
            {language === 'nl' ? (
              <>
                <li className="mb-1">
                  <div className="px-3 py-2 text-xs uppercase text-cyan-500/70 font-semibold">
                    {t('common.calculators')}
                  </div>
                  <div className="space-y-1">
                    {[
                      { id: 'percentage', name: t('tools.percentage'), icon: 'percent' },
                      { id: 'pronunciation', name: t('tools.pronunciation'), icon: 'text' },
                      { id: 'sqrt', name: t('tools.sqrt'), icon: 'function-square' },
                      { id: 'dollarEuro', name: t('tools.dollarEuro'), icon: 'arrow-left-right' },
                    ].map((calc) => (
                      <CalculatorLink
                        key={calc.id}
                        id={calc.id}
                        className={`flex items-center w-full text-left px-3 py-2 text-sm rounded
                          ${activeCalculator === calc.id
                            ? 'bg-cyan-900/40 text-cyan-300 border-l-2 border-cyan-400 dark:bg-cyan-900/40 dark:text-cyan-300 bg-cyan-100 text-cyan-900 border-cyan-600'
                            : 'text-gray-600 dark:text-gray-400 hover:text-cyan-900 dark:hover:text-cyan-200 hover:bg-gray-200 dark:hover:bg-[var(--hover-bg)]'} 
                          transition-all duration-200`}
                      >
                        <span className="mr-2 opacity-70">
                          {getIcon(calc.icon, 16)}
                        </span>
                        {calc.name}
                      </CalculatorLink>
                    ))}
                  </div>
                </li>
              </>
            ) : (
              // TR Mode: Show full categories
              <>
                <li className="mb-1">
                  <CalculatorLink
                    id="calcApp"
                    className={`w-full text-left px-3 py-2 rounded flex items-center text-sm 
                  ${activeCalculator === 'calcApp'
                        ? 'bg-cyan-900/40 text-cyan-300 border-l-2 border-cyan-400 dark:bg-cyan-900/40 dark:text-cyan-300 bg-cyan-100 text-cyan-900 border-cyan-600'
                        : 'hover:bg-gray-200 dark:hover:bg-[var(--hover-bg)] text-gray-600 dark:text-gray-400 hover:text-cyan-900 dark:hover:text-cyan-200'} 
                  transition-all duration-200`}
                  >
                    <span className="mr-2 opacity-80">
                      <Calculator size={20} className="text-cyan-400" />
                    </span>
                    {t('home.calculator')}
                  </CalculatorLink>
                </li>

                {/* Diğer kategoriler için */}
                {categories
                  .filter(category => category.id !== 'home' && category.calculators.length > 0)
                  .map((category) => (
                    <li key={category.id} className="mb-1">
                      <button
                        onClick={() => toggleCategory(category.id)}
                        className={`w-full text-left px-3 py-2 rounded flex items-center justify-between text-sm
                      transition-all duration-200 bg-[var(--sidebar-bg)] hover:bg-gray-200 dark:hover:bg-[var(--hover-bg)] border-b border-[var(--border-color)]
                      ${openCategory === category.id
                            ? 'text-cyan-800 dark:text-cyan-300 font-medium'
                            : 'text-gray-600 dark:text-gray-400 hover:text-cyan-900 dark:hover:text-cyan-200'}`}
                      >
                        <div className="flex items-center">
                          <span className="mr-2 opacity-80">{category.icon}</span>
                          {category.title}
                        </div>
                        {openCategory === category.id ? (
                          <ChevronUp size={16} className="text-cyan-400" />
                        ) : (
                          <ChevronDown size={16} className="text-cyan-400" />
                        )}
                      </button>
                      {openCategory === category.id && (
                        <div className="pl-2 space-y-1 py-1">
                          {category.calculators.map((calc: any) => (
                            <CalculatorLink
                              key={calc.id}
                              id={calc.id}
                              className={`flex items-center w-full text-left px-3 py-2 text-sm rounded
                            ${activeCalculator === calc.id
                                  ? 'bg-cyan-900/40 text-cyan-300 border-l-2 border-cyan-400 dark:bg-cyan-900/40 dark:text-cyan-300 bg-cyan-100 text-cyan-900 border-cyan-600'
                                  : 'text-gray-600 dark:text-gray-400 hover:text-cyan-900 dark:hover:text-cyan-200 hover:bg-gray-200 dark:hover:bg-[var(--hover-bg)]'} 
                            transition-all duration-200`}
                            >
                              <span className="mr-2 opacity-70">
                                {getIcon(calc.icon, 16)}
                              </span>
                              {calc.name}
                            </CalculatorLink>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
              </>
            )}
          </ul>
        )}
      </nav>

      <div className="mt-auto pt-6 text-xs text-gray-400 border-t border-cyan-800/30 flex flex-col items-center gap-4">
        <div>
          {/* Copyright hidden or minimal */}
          <p className="opacity-50">Hesaplama.Vip © 2025</p>
        </div>

        {/* Language Switcher - Only if calculator supports NL */}
        {/* Dil seçimi kutusu */}
        {(activeCalculator === 'home' || (routeConfig[activeCalculator as keyof typeof routeConfig] && activeCalculator !== 'goldCalculator')) && (
          <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg p-1">
            <button
              onClick={() => {
                const url = getUrlForId(activeCalculator, 'tr');
                if (url) window.location.href = url; // Force reload to ensure clean state or use navigate
              }}
              className={`px-2 py-1 rounded text-xs font-bold transition-colors ${language === 'tr' ? 'bg-cyan-700 text-white' : 'text-gray-400 hover:text-white'}`}
            >
              TR
            </button>
            <div className="w-px h-3 bg-gray-600"></div>
            <button
              onClick={() => {
                const url = getUrlForId(activeCalculator, 'nl');
                if (url) window.location.href = url;
              }}
              className={`px-2 py-1 rounded text-xs font-bold transition-colors ${language === 'nl' ? 'bg-orange-700 text-white' : 'text-gray-400 hover:text-white'}`}
            >
              NL
            </button>
          </div>
        )}
      </div>
    </div >
  );
};

export default Sidebar;